import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { api } from "@/lib/api";
import { live, LIVE_SLOW_MS } from "@/lib/live-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageSpinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { addDaysToDateKey, localDateKey } from "@/lib/datetime";
import { readLoad, formatLoad } from "./load-basis";

interface WeekLoadCell {
    stops: number;
    bookedMinutes: number;
    /** #630: points booked that day — the Day board's primary weight (#587). */
    bookedPoints?: number;
    capacityMinutes: number;
    topJurisdictions: string[];
}

interface WeekLoadTech {
    id: string;
    name: string;
    cells: Record<string, WeekLoadCell>;
    dailyCapacityMinutes: number;
    /** #828 M9: true = paired helper, not an independently routed tech. */
    isFieldHelper?: boolean;
    /** #630: what this tech's points are measured against. Null = no target
     *  set, so their cells fall back to reading in hours. */
    dailyPointsTarget?: number | null;
    dailyPointsCap?: number | null;
}

interface WeekLoadResponse {
    dateFrom: string;
    dateTo: string;
    techs: WeekLoadTech[];
    defaults: { dailyCapacityMinutes: number };
}

interface WeekHeatmapProps {
    /** Anchor date — week starts on Monday containing this date. */
    selectedDate: string;
    /** Open the day view focused on a date. */
    onOpenDay: (dateKey: string) => void;
    /** Number of days to show: 5 (Mon-Fri) or 7 (Mon-Sun). */
    daysToShow?: 5 | 7;
    /**
     * Drop handler that turns the heatmap into a draggable board. When a job
     * (dragged from the backlog panel or a swimlane — payload is the bare
     * jobId in `text/plain`) is released on a tech-row x day-cell, the shell
     * schedules it for that tech on that day at a default start time. When
     * omitted the board stays read-only.
     */
    onScheduleDrop?: (jobId: string, techId: string, dateKey: string) => void;
}

function startOfWeekMonday(dateKey: string): string {
    const [y, m, d] = dateKey.split("-").map((n) => Number.parseInt(n, 10));
    const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
    const day = date.getDay(); // 0 = Sun .. 6 = Sat
    const offset = day === 0 ? -6 : 1 - day; // shift back to Monday
    date.setDate(date.getDate() + offset);
    return localDateKey(date);
}

function dayLabel(dateKey: string): { weekday: string; numeric: string } {
    const [y, m, d] = dateKey.split("-").map((n) => Number.parseInt(n, 10));
    const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
    return {
        weekday: dt.toLocaleDateString(undefined, { weekday: "short" }),
        numeric: dt.toLocaleDateString(undefined, { month: "numeric", day: "numeric" }),
    };
}

function ratioTone(ratio: number): {
    cell: string;
    badge: string;
    label: string;
} {
    if (ratio === 0) {
        return {
            cell: "bg-muted/30 text-foreground/80",
            badge: "bg-muted text-foreground/80",
            label: "Open",
        };
    }
    if (ratio < 0.5)
        return {
            cell: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-100",
            badge: "bg-emerald-700 text-white",
            label: "Light",
        };
    if (ratio < 0.85)
        return {
            cell: "bg-emerald-200 dark:bg-emerald-800/50 text-emerald-900 dark:text-emerald-50",
            badge: "bg-emerald-700 text-white",
            label: "Healthy",
        };
    if (ratio <= 1)
        return {
            cell: "bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-50",
            badge: "bg-amber-700 text-white",
            label: "Full",
        };
    if (ratio <= 1.2)
        return {
            cell: "bg-orange-300 dark:bg-orange-800/70 text-orange-950 dark:text-orange-50",
            badge: "bg-orange-600 text-white",
            label: "Over",
        };
    return {
        cell: "bg-red-400 dark:bg-red-700/80 text-red-50",
        badge: "bg-red-700 text-white",
        // #828 M7: "Critical" read as urgent-work when it means TOO MUCH work.
        label: "Overbooked",
    };
}

/** #828 M7: the load words, defined where they appear — five undefined words
 *  (two of them green) forced new staff to guess. Rendered under the grid. */
const LOAD_LEGEND: Array<{ label: string; meaning: string; badge: string }> = [
    { label: "Open", meaning: "no jobs booked", badge: "bg-muted text-foreground/80" },
    { label: "Light", meaning: "under half booked", badge: "bg-emerald-700 text-white" },
    { label: "Healthy", meaning: "under 85% booked", badge: "bg-emerald-700 text-white" },
    { label: "Full", meaning: "at capacity", badge: "bg-amber-700 text-white" },
    { label: "Over", meaning: "up to 20% past capacity", badge: "bg-orange-600 text-white" },
    { label: "Overbooked", meaning: "more than 20% past capacity", badge: "bg-red-700 text-white" },
];

export function WeekHeatmap({ selectedDate, onOpenDay, daysToShow = 5, onScheduleDrop }: WeekHeatmapProps) {
    const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(selectedDate));
    // Keep the week board in sync with the day-view date picker so navigating
    // the day and flipping to the week don't drift onto different weeks.
    useEffect(() => {
        setWeekStart(startOfWeekMonday(selectedDate));
    }, [selectedDate]);
    // 5 (Mon–Fri) or 7 (Mon–Sun) — toggleable so weekend work is visible.
    const [visibleDays, setVisibleDays] = useState<5 | 7>(daysToShow);
    const days = useMemo(
        () => Array.from({ length: visibleDays }, (_, i) => addDaysToDateKey(weekStart, i)),
        [weekStart, visibleDays],
    );
    const dateFrom = days[0]!;
    const dateTo = days[days.length - 1]!;

    const [expanded, setExpanded] = useState<{ techId: string; dayKey: string } | null>(null);
    // Drop-target highlight while a job is dragged over a cell.
    const [dragOverCell, setDragOverCell] = useState<{ techId: string; dayKey: string } | null>(null);
    const dragEnabled = !!onScheduleDrop;

    const weekQuery = useQuery({
        queryKey: ["dispatch", "week-load", dateFrom, dateTo],
        ...live(LIVE_SLOW_MS),
        queryFn: () => api.get<WeekLoadResponse>(`/api/dispatch/week-load?date_from=${dateFrom}&date_to=${dateTo}`),
    });

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setWeekStart(addDaysToDateKey(weekStart, -visibleDays))}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="text-sm font-medium">
                        Week of {dayLabel(weekStart).weekday} {dayLabel(weekStart).numeric}
                        {" — "}
                        {dayLabel(dateTo).weekday} {dayLabel(dateTo).numeric}
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setWeekStart(addDaysToDateKey(weekStart, visibleDays))}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setWeekStart(startOfWeekMonday(localDateKey(new Date())))}
                    >
                        This week
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setVisibleDays((d) => (d === 5 ? 7 : 5))}
                        title={visibleDays === 5 ? "Show weekends (Mon–Sun)" : "Hide weekends (Mon–Fri)"}
                    >
                        {visibleDays === 5 ? "Show weekends" : "Hide weekends"}
                    </Button>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-foreground/80">
                    <Info className="h-3 w-3" />
                    <span>
                        Cells colored by booked vs. capacity. Click any cell to peek at the day
                        {dragEnabled ? ", or drag a job onto a cell to schedule it at 9:00." : "."}
                    </span>
                </div>
            </div>

            {weekQuery.isLoading ? (
                <div className="flex h-64 items-center justify-center rounded-lg border bg-card">
                    <PageSpinner />
                </div>
            ) : (weekQuery.data?.techs.length ?? 0) === 0 ? (
                <div className="rounded-lg border bg-card p-8 text-center text-sm text-foreground/80">
                    No jobs scheduled for this date.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border bg-card">
                    <table className="w-full table-fixed border-collapse text-xs">
                        <thead>
                            <tr className="border-b bg-muted/30">
                                <th className="sticky left-0 z-10 w-44 border-r bg-muted/30 px-3 py-2 text-left font-semibold text-foreground/80">
                                    Technician
                                </th>
                                {days.map((d) => {
                                    const { weekday, numeric } = dayLabel(d);
                                    return (
                                        <th
                                            key={d}
                                            className="border-r px-2 py-2 text-center font-semibold last:border-r-0"
                                        >
                                            <div className="text-foreground">{weekday}</div>
                                            <div className="text-[10px] text-foreground/80">{numeric}</div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {(weekQuery.data?.techs ?? []).map((tech) => (
                                <tr key={tech.id} className="border-b last:border-b-0">
                                    <td className="sticky left-0 z-10 border-r bg-card px-3 py-2 text-left font-medium">
                                        <div className="truncate">{tech.name}</div>
                                        {/* #828 M9: a helper is paired onto a lead's
                                            route, not routed with capacity of their
                                            own — never render them as a full tech. */}
                                        {tech.isFieldHelper ? (
                                            <div className="text-[10px] text-amber-600 dark:text-amber-400">
                                                helper — paired, not routed
                                            </div>
                                        ) : (
                                            <div className="text-[10px] text-foreground/80">
                                                cap {(tech.dailyCapacityMinutes / 60).toFixed(1)}h/day
                                            </div>
                                        )}
                                    </td>
                                    {days.map((d) => {
                                        const cell = tech.cells[d];
                                        // #630: read the same way the Day board
                                        // does — points first, hours only when
                                        // this tech has no points target.
                                        const load = readLoad({
                                            bookedPoints: cell?.bookedPoints,
                                            pointsTarget: tech.dailyPointsTarget,
                                            pointsCap: tech.dailyPointsCap,
                                            bookedMinutes: cell?.bookedMinutes ?? 0,
                                            capacityMinutes: cell?.capacityMinutes ?? tech.dailyCapacityMinutes,
                                        });
                                        // Past the hard cap is red regardless of
                                        // where the ratio lands — a cap set well
                                        // above target would otherwise read
                                        // "Full" while genuinely overbooked.
                                        const tone = load.overCap ? ratioTone(999) : ratioTone(load.ratio);
                                        const isExpanded = expanded?.techId === tech.id && expanded?.dayKey === d;
                                        const isDragOver =
                                            dragOverCell?.techId === tech.id && dragOverCell?.dayKey === d;
                                        return (
                                            <td
                                                key={d}
                                                className={cn(
                                                    "border-r p-1 align-top last:border-r-0",
                                                    isExpanded && "ring-2 ring-primary",
                                                    isDragOver && "ring-2 ring-inset ring-primary bg-primary/10",
                                                )}
                                                onDragOver={
                                                    dragEnabled
                                                        ? (e) => {
                                                              e.preventDefault();
                                                              e.dataTransfer.dropEffect = "move";
                                                              if (!isDragOver) {
                                                                  setDragOverCell({ techId: tech.id, dayKey: d });
                                                              }
                                                          }
                                                        : undefined
                                                }
                                                onDragLeave={
                                                    dragEnabled
                                                        ? (e) => {
                                                              // Only clear when the pointer actually leaves the
                                                              // cell, not when it crosses a child element.
                                                              if (
                                                                  !e.currentTarget.contains(
                                                                      e.relatedTarget as Node | null,
                                                                  )
                                                              ) {
                                                                  setDragOverCell((prev) =>
                                                                      prev?.techId === tech.id && prev?.dayKey === d
                                                                          ? null
                                                                          : prev,
                                                                  );
                                                              }
                                                          }
                                                        : undefined
                                                }
                                                onDrop={
                                                    dragEnabled
                                                        ? (e) => {
                                                              e.preventDefault();
                                                              setDragOverCell(null);
                                                              const jobId = e.dataTransfer.getData("text/plain");
                                                              if (jobId) onScheduleDrop?.(jobId, tech.id, d);
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setExpanded(isExpanded ? null : { techId: tech.id, dayKey: d })
                                                    }
                                                    className={cn(
                                                        "flex h-full w-full flex-col gap-1 rounded-md p-2 text-left transition-shadow hover:shadow-sm",
                                                        tone.cell,
                                                    )}
                                                >
                                                    <div className="flex items-center justify-between gap-1">
                                                        <span className="text-[11px] font-semibold">
                                                            {cell?.stops ?? 0} stops
                                                        </span>
                                                        <Badge
                                                            className={cn(
                                                                "px-1 py-0 text-[9px] font-semibold",
                                                                tone.badge,
                                                            )}
                                                            variant="outline"
                                                        >
                                                            {tone.label}
                                                        </Badge>
                                                    </div>
                                                    <div className="text-[10px] font-medium opacity-95">
                                                        {formatLoad(load)}
                                                    </div>
                                                    {/* Hours ride along as the thin
                                                        secondary line, exactly as on
                                                        the Day board (#587). Omitted
                                                        when hours ARE the primary
                                                        read, so it never doubles up. */}
                                                    {load.basis === "points" && (
                                                        <div className="text-[10px] opacity-75">
                                                            {((cell?.bookedMinutes ?? 0) / 60).toFixed(1)}h
                                                        </div>
                                                    )}
                                                    {cell && cell.topJurisdictions.length > 0 && (
                                                        <div className="truncate text-[10px] opacity-85">
                                                            {cell.topJurisdictions.slice(0, 2).join(" · ")}
                                                        </div>
                                                    )}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* #828 M7: the load words defined on the view where they appear. */}
            <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground/70"
                data-testid="load-legend"
            >
                {LOAD_LEGEND.map((l) => (
                    <span key={l.label} className="inline-flex items-center gap-1">
                        <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold", l.badge)}>
                            {l.label}
                        </span>
                        {l.meaning}
                    </span>
                ))}
                {/* #630: some rows read in points and some in hours, depending on
                    whether that tech has a points target. Saying so beats leaving
                    the office to work out why the units differ down the column. */}
                <span className="basis-full text-foreground/60">
                    Cells show <strong>points</strong> against a tech&apos;s daily target, with hours underneath. A tech
                    with no points target reads in hours instead.
                </span>
            </div>

            {expanded && (
                <ExpandedCellPanel
                    techId={expanded.techId}
                    dayKey={expanded.dayKey}
                    techs={weekQuery.data?.techs ?? []}
                    onOpenDay={onOpenDay}
                    onClose={() => setExpanded(null)}
                />
            )}
        </div>
    );
}

interface ExpandedCellPanelProps {
    techId: string;
    dayKey: string;
    techs: WeekLoadTech[];
    onOpenDay: (dateKey: string) => void;
    onClose: () => void;
}

function ExpandedCellPanel({ techId, dayKey, techs, onOpenDay, onClose }: ExpandedCellPanelProps) {
    const tech = techs.find((t) => t.id === techId);
    const cell = tech?.cells[dayKey];
    const { weekday, numeric } = dayLabel(dayKey);
    return (
        <div className="rounded-lg border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h4 className="text-sm font-semibold">
                        {tech?.name} — {weekday} {numeric}
                    </h4>
                    <p className="text-xs text-foreground/80">
                        {cell ? (
                            <>
                                {cell.stops} stop{cell.stops === 1 ? "" : "s"} · {(cell.bookedMinutes / 60).toFixed(1)}h
                                booked of {(cell.capacityMinutes / 60).toFixed(1)}h capacity
                            </>
                        ) : (
                            "No jobs scheduled"
                        )}
                    </p>
                    {cell && cell.topJurisdictions.length > 0 && (
                        <p className="mt-1 text-xs text-foreground/80">
                            Working in: {cell.topJurisdictions.join(", ")}
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button size="sm" onClick={() => onOpenDay(dayKey)}>
                        View day in detail
                    </Button>
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </div>
    );
}
